import React from 'react';
import { MapPin, Euro, Music, Wrench, Car, Train, Truck, Bike, Bus, Plane } from 'lucide-react';
import { cn } from '../../lib/utils';

const transitIcons = {
  car: Car,
  public_transport: Train,
  van: Truck,
  bike: Bike,
  tour_bus: Bus,
  flight_ready: Plane,
};

const experienceLabels = {
  // Musician
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  experienced: 'Experienced',
  senior: 'Senior',
  touring_pro: 'Touring Pro',
  // Tech
  junior: 'Junior',
  medior: 'Medior',
};

const rateTypeLabels = {
  flat: 'Flat',
  hourly: '/hr',
  part_of_day: '/part',
};

export function ProfilePreviewCard({ profile, profileType }) {
  const TransitIcon = transitIcons[profile.primaryTransitMode] || Car;
  
  // Get primary item based on profile type
  const primaryItem = profileType === 'musician' 
    ? (profile.instruments?.[0] || 'Musician')
    : (profile.specialties?.[0] || 'Tech Crew');

  const experienceLevel = profileType === 'musician'
    ? profile.musicianExperienceLevel
    : profile.techExperienceLevel;

  return (
    <div className="surface-card p-4 rounded-xl space-y-3">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {profileType === 'musician' ? (
              <Music className="h-4 w-4 text-amber-400 flex-shrink-0" />
            ) : (
              <Wrench className="h-4 w-4 text-amber-400 flex-shrink-0" />
            )}
            <span className="font-semibold text-white truncate">
              {profile.displayName || 'Your Name'}
            </span>
          </div>
          <div className="flex items-center gap-1 text-white/40 text-xs mt-1">
            <MapPin className="h-3 w-3" />
            <span>{profile.city || 'City'}</span>
          </div>
        </div>
        
        {/* Rate */}
        {profile.rateAmount && (
          <div className="text-right flex-shrink-0">
            <div className="text-amber-400 font-semibold">
              €{profile.rateAmount}
            </div>
            <div className="text-white/30 text-xs">
              {rateTypeLabels[profile.onboardingRateType] || ''}
            </div>
          </div>
        )}
      </div>

      {/* Primary Item */}
      <div className="text-sm text-white/70">
        {primaryItem}
        {profile.instruments?.length > 1 && profileType === 'musician' && (
          <span className="text-white/40"> +{profile.instruments.length - 1}</span>
        )}
        {profile.specialties?.length > 1 && profileType === 'tech' && (
          <span className="text-white/40"> +{profile.specialties.length - 1}</span>
        )}
      </div>

      {/* Tags Row */}
      <div className="flex flex-wrap gap-1.5">
        {experienceLevel && (
          <span className="px-2 py-0.5 bg-white/[0.06] rounded text-xs text-white/60">
            {experienceLabels[experienceLevel] || experienceLevel}
          </span>
        )}
        {profile.travelRadiusKm && (
          <span className="px-2 py-0.5 bg-white/[0.06] rounded text-xs text-white/60 flex items-center gap-1">
            <TransitIcon className="h-3 w-3" />
            {profile.travelRadiusKm}km
          </span>
        )}
        {profile.eventTypes?.slice(0, 2).map((type) => (
          <span key={type} className="px-2 py-0.5 bg-white/[0.06] rounded text-xs text-white/60">
            {type}
          </span>
        ))}
      </div>

      {/* Completeness indicator */}
      <div className="pt-2 border-t border-white/[0.06]">
        <div className="flex items-center justify-between text-xs">
          <span className="text-white/40">Profile completeness</span>
          <span className={cn(
            'font-medium',
            profile.fundamentalsCompleted ? 'text-emerald-400' : 'text-amber-400'
          )}>
            {calculateCompleteness(profile, profileType)}%
          </span>
        </div>
        <div className="mt-1.5 h-1 bg-white/[0.06] rounded-full overflow-hidden">
          <div 
            className={cn(
              'h-full rounded-full transition-all duration-500',
              profile.fundamentalsCompleted ? 'bg-emerald-400' : 'bg-amber-400'
            )}
            style={{ width: `${calculateCompleteness(profile, profileType)}%` }}
          />
        </div>
      </div>
    </div>
  );
}

function calculateCompleteness(profile, profileType) {
  let filled = 0;
  const total = 5;

  if (profileType === 'musician') {
    if (profile.instruments?.length > 0) filled++;
    if (profile.eventTypes?.length > 0) filled++;
    if (profile.primaryTransitMode && profile.travelRadiusKm) filled++;
    if (profile.rateAmount && profile.onboardingRateType) filled++;
    if (profile.musicianExperienceLevel) filled++;
  } else {
    if (profile.specialties?.length > 0) filled++;
    if (profile.eventTypes?.length > 0) filled++;
    if (profile.primaryTransitMode && profile.travelRadiusKm) filled++;
    if (profile.rateAmount && profile.onboardingRateType) filled++;
    if (profile.techExperienceLevel) filled++;
  }

  return Math.round((filled / total) * 100);
}
